// Painel de detalhe do nó, portado de graph.js:518-649.
//
// Dois painéis, e não um com ifs: nó de código tem arquivo, assinatura e
// decorator; nó de memória tem autor, carimbo de revisão e o que ele toca.
// Misturar os dois produziria um painel com metade dos campos sempre vazios.
import { useState } from 'react';
import {
  KIND, KIND_LABEL, MEMORY_KINDS, REL_LABEL, type Aresta, type GrafoBruto, type Modo, type No,
} from './nucleo';
import type { Decisao } from '../api/consultas';
import { acionavel } from '../componentes/acionavel';

export interface AcoesDoDetalhe {
  abrirDoc: (chave: string) => void;
  abrirImpacto: (chave: string) => void;
  abrirSessao: (id: string) => void;
  irParaAba: (aba: string) => void;
  copiarComando: (chave: string) => void;
  focarNo: (chave: string) => void;
  isolar: () => void;
  verTudo: () => void;
  fechar: () => void;
}

interface Props {
  no: No;
  grafo: GrafoBruto | null;
  modo: Modo;
  profundidade: number;
  notasPorArquivo: Record<string, number>;
  temDoc: (chave: string) => boolean;
  dono: (chave: string) => string | null;
  decisoesDe: (chave: string) => Decisao[];
  acoes: AcoesDoDetalhe;
  /** modo cobertura: escrever nota ou abrir lacuna sem sair do grafo */
  aoEscreverNota?: (chave: string, texto: string) => Promise<void>;
  aoAbrirLacuna?: (chave: string, texto: string) => Promise<void>;
}

const Linha = ({ rotulo, children }: { rotulo: string; children: React.ReactNode }) => (
  <div className="g-row"><b>{rotulo}</b>{children}</div>
);

const Ficha = ({ chave, texto, aoClicar }: { chave: string; texto: string; aoClicar: (k: string) => void }) => (
  <span className="g-chip go" title={texto} {...acionavel(() => aoClicar(chave))}>
    {texto}
  </span>
);

export function Detalhe(p: Props) {
  const { no: n, acoes } = p;
  const deMemoria = p.modo === 'memoria' || MEMORY_KINDS.includes(n.kind);
  const cor = `var(${KIND[n.kind] || '--brand'})`;
  const saem = (p.grafo?.edges || []).filter((e: Aresta) => e.src === n.node_key);
  const entram = (p.grafo?.edges || []).filter((e: Aresta) => e.dst === n.node_key);
  const ponta = (chave: string) => String(chave).replace(/^pkg:/, '').split('/').pop() || chave;

  return (
    <div className="g-detail show">
      <button className="g-close" type="button" title="fechar" onClick={acoes.fechar}>×</button>
      <h4>{String(n.label || n.node_key).replace(/^pkg:/, '')}</h4>
      <span className="g-kind" style={{ background: cor }}>{KIND_LABEL[n.kind] || n.kind}</span>
      {!deMemoria && !!(n.file && p.notasPorArquivo[n.file]) && (
        <span className="g-kind" style={{ background: 'var(--brand-2)' }}>
          {p.notasPorArquivo[n.file!]} nota{p.notasPorArquivo[n.file!] > 1 ? 's' : ''}
        </span>
      )}

      {deMemoria ? (
        <>
          {n.author && <Linha rotulo="autor"><code>{n.author}</code></Linha>}
          {n.review_state && <Linha rotulo="carimbo"><code>{n.review_state}</code></Linha>}
          {n.status && <Linha rotulo="estado"><code>{n.status}</code></Linha>}
          {n.events != null && <Linha rotulo="eventos no diário"><code>{n.events}</code></Linha>}
          {n.unit_key && n.unit_key !== n.node_key && (
            <Linha rotulo="sobre"><Ficha chave={n.unit_key} texto={n.unit_key} aoClicar={acoes.focarNo} /></Linha>
          )}
          {n.memory != null && <Linha rotulo="artefatos de memória"><code>{n.memory}</code></Linha>}
          {n.drift && (
            <Linha rotulo="deriva">
              <span className="muted" style={{ fontSize: 12 }}>esta decisão cita código que não existe mais</span>
            </Linha>
          )}
          {n.orphan && (
            <Linha rotulo="atenção">
              <span className="muted" style={{ fontSize: 12 }}>
                a memória aponta para uma unidade que não está no acervo
              </span>
            </Linha>
          )}
        </>
      ) : (
        <>
          {n.file && <Linha rotulo="arquivo"><code>{n.file}{n.line ? `:${n.line}` : ''}</code></Linha>}
          {n.http && <Linha rotulo="rota"><code>{`${n.http.method} ${n.http.path}`}</code></Linha>}
          {n.signature && <Linha rotulo="assinatura"><code>{n.signature}</code></Linha>}
          {!!n.decorators?.length && (
            <Linha rotulo="decorators">
              {n.decorators.map((d) => <span className="g-chip" key={d}>@{d}</span>)}
            </Linha>
          )}
          {n.meta?.handler && <Linha rotulo="handler"><code>{n.meta.handler}</code></Linha>}
          {n.meta?.basePath && <Linha rotulo="base path"><code>{n.meta.basePath}</code></Linha>}
          {n.meta?.returns && <Linha rotulo="retorna"><code>{n.meta.returns}</code></Linha>}
          {!!n.meta?.fields?.length && (
            <Linha rotulo="campos">
              {n.meta.fields.map((f) => <span className="g-chip" key={f}>{f}</span>)}
            </Linha>
          )}
          {n.meta?.loc != null && <Linha rotulo="linhas"><code>{n.meta.loc}</code></Linha>}
          {n.wt && <Linha rotulo="origem"><code>working tree (não commitado)</code></Linha>}
        </>
      )}

      {!!saem.length && (
        <Linha rotulo={`${deMemoria ? 'aponta para' : 'depende de'} (${saem.length})`}>
          {saem.slice(0, 14).map((e, i) => (
            <Ficha key={`${e.dst}-${e.rel}-${i}`} chave={e.dst}
              texto={`${REL_LABEL[e.rel] || e.rel}: ${ponta(e.dst)}`} aoClicar={acoes.focarNo} />
          ))}
        </Linha>
      )}
      {!!entram.length && (
        <Linha rotulo={`${deMemoria ? 'apontado por' : 'usado por'} (${entram.length})`}>
          {entram.slice(0, 14).map((e, i) => (
            <Ficha key={`${e.src}-${e.rel}-${i}`} chave={e.src}
              texto={`${REL_LABEL[e.rel] || e.rel}: ${ponta(e.src)}`} aoClicar={acoes.focarNo} />
          ))}
        </Linha>
      )}
      {!deMemoria && !saem.length && !entram.length && (
        <Linha rotulo="relações">
          <span className="muted" style={{ fontSize: 12 }}>
            nenhuma extraída pelo AST — pode ser uso dinâmico ou arquivo isolado
          </span>
        </Linha>
      )}

      {/* dono e decisões vêm da camada de conhecimento: o grafo sozinho não
          responde "a quem pergunto" nem "o que já foi decidido sobre isto" */}
      {!deMemoria && n.file && (
        <Linha rotulo="dono"><code>{p.dono(n.file) || 'não declarado'}</code></Linha>
      )}
      {!deMemoria && !!p.decisoesDe(n.node_key).length && (
        <Linha rotulo="decisões que governam">
          {p.decisoesDe(n.node_key).slice(0, 6).map((d) => (
            <Ficha key={d.unit_key} chave={d.unit_key} texto={d.title} aoClicar={acoes.abrirDoc} />
          ))}
        </Linha>
      )}

      <div className="g-acts">
        {deMemoria ? (
          <>
            {['decisao', 'runbook', 'resumo'].includes(n.kind) && (
              <button type="button" onClick={() => acoes.abrirDoc(n.node_key)}>abrir texto</button>
            )}
            {n.kind === 'sessao' && (
              <button type="button" onClick={() => acoes.abrirSessao(String(n.node_key).replace(/^sessao:/, ''))}>
                ver sessão
              </button>
            )}
            {n.kind === 'proposta' && (
              <button type="button" onClick={() => acoes.irParaAba('review')}>ir para revisão</button>
            )}
            {n.kind === 'lacuna' && (
              <button type="button" onClick={() => acoes.irParaAba('health')}>ir para saúde</button>
            )}
            {n.kind === 'nota' && n.unit_key && (
              <button type="button" onClick={() => acoes.abrirDoc(n.unit_key!)}>abrir arquivo anotado</button>
            )}
            {(n.kind === 'arquivo' || n.kind === 'unidade') && (
              <>
                <button type="button" onClick={() => acoes.abrirDoc(n.unit_key || n.node_key)}>abrir doc</button>
                <button type="button" onClick={() => acoes.abrirImpacto(n.unit_key || n.node_key)}>ver impacto</button>
              </>
            )}
          </>
        ) : (
          <>
            {n.file && p.temDoc(n.file) && (
              <button type="button" onClick={() => acoes.abrirDoc(n.file!)}>abrir doc</button>
            )}
            <button type="button" onClick={() => acoes.abrirImpacto(n.file || n.node_key)}>ver impacto</button>
            {n.file && (
              <button type="button" onClick={() => acoes.copiarComando(n.file!)}>copiar eng doc</button>
            )}
          </>
        )}
        <button type="button" onClick={acoes.isolar}>isolar vizinhança</button>
        {!!p.profundidade && <button type="button" onClick={acoes.verTudo}>ver tudo</button>}
      </div>

      {p.modo === 'cobertura' && !n.memory && (n.file || n.kind === 'file') && p.aoEscreverNota && p.aoAbrirLacuna && (
        <Compositor chave={n.file || n.node_key} aoEscreverNota={p.aoEscreverNota} aoAbrirLacuna={p.aoAbrirLacuna} />
      )}
    </div>
  );
}

/**
 * Arquivo sem nenhuma memória: o clique já oferece escrever.
 *
 * É o ponto do modo cobertura — mostrar o vazio e baixar o custo de preenchê-lo
 * no mesmo lugar, em vez de mandar o dev procurar a aba de notas.
 */
function Compositor({ chave, aoEscreverNota, aoAbrirLacuna }: {
  chave: string;
  aoEscreverNota: (chave: string, texto: string) => Promise<void>;
  aoAbrirLacuna: (chave: string, texto: string) => Promise<void>;
}) {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async (f: (chave: string, texto: string) => Promise<void>) => {
    setEnviando(true);
    try {
      await f(chave, texto.trim());
      setTexto('');
    } finally {
      setEnviando(false);
    }
  };
  return (
    <div className="g-row">
      <b>ninguém escreveu sobre este arquivo</b>
      <label className="sr-only" htmlFor="cov-txt">o que o código não mostra neste arquivo</label>
      <textarea
        id="cov-txt" rows={3} value={texto} onChange={(e) => setTexto(e.target.value)}
        placeholder="o que o código não mostra aqui (runtime, regra de negócio, gotcha)…"
        style={{
          width: '100%', margin: '6px 0', font: 'inherit', fontSize: 12, padding: 6,
          border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg)', color: 'var(--ink)',
        }}
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn sm" type="button" disabled={enviando} onClick={() => enviar(aoEscreverNota)}>
          salvar nota
        </button>
        <button className="btn ghost sm" type="button" disabled={enviando} onClick={() => enviar(aoAbrirLacuna)}>
          abrir lacuna
        </button>
      </div>
    </div>
  );
}
